import { NetworkClient } from "./net/NetworkClient.js";
import { installDiagnostics } from "./diagnostics.js";
import { t } from "./i18n/index.js";
import { IntroVideo } from "./ui/IntroVideo.js";
import { JoinPanel } from "./ui/JoinPanel.js";
import { LoadingScreen } from "./ui/LoadingScreen.js";
import { SettingsPanel } from "./ui/SettingsPanel.js";
import { el } from "./ui/dom.js";
import "./style.css";

/**
 * Boot order matters on a ₹10k phone over 4G.
 *
 * The opening film runs first, over a join screen that is already built and
 * waiting behind it. Phaser — by far the biggest thing we ship — is loaded
 * only once the fan shows a sign of life, so the engine download and its parse
 * overlap with the parts of the flow that are not waiting on us: watching the
 * film, and typing a name.
 */

const uiRoot = document.getElementById("ui-root");
if (!uiRoot) throw new Error("#ui-root is missing from the page");

installDiagnostics();

if (!hasWebGL()) {
  showUnsupported(uiRoot);
} else {
  void boot(uiRoot);
}

async function boot(root: HTMLElement): Promise<void> {
  const net = new NetworkClient();
  const loading = new LoadingScreen(root);
  const settings = new SettingsPanel(root);
  settings.setVisible(true);

  // Stood up when the film is over rather than now: parsing Phaser is about a
  // second of main-thread work and would hitch the one thing on screen.
  let engine: EngineLoader | null = null;
  const loadEngine = (): EngineLoader => (engine ??= engineLoader(net, loading));

  new JoinPanel(root, async ({ name, avatarId }) => {
    // Curtain back up: this is the one wait the fan actually asked for.
    loading.show();
    try {
      // Submitting is itself engagement. Assistive tech and voice control can
      // activate a button without ever firing pointerdown, and a join that hangs
      // waiting for a gesture that never comes is the worst possible failure.
      const loader = loadEngine();
      loader.start();
      await loader.ready;
      await net.join(name, avatarId);
    } catch (err) {
      // Put the card back, or the failure message renders behind the curtain.
      loading.hide();
      throw err;
    }
    loading.finish();
  });

  // The join card is plain DOM and is ready the moment it is appended. Get the
  // curtain out of its way immediately: it is opaque and full-screen, so
  // leaving it up hides the very form whose typing time we are trying to
  // overlap the engine parse with.
  loading.set("ui");
  loading.hide();

  // The film covers everything, so the join card and the curtain behind it are
  // built and settled before its first frame.
  new IntroVideo(root, (played) => {
    const loader = loadEngine();
    // A film that actually ran and then ended — watched through or skipped — is
    // the sign of life the engine has been waiting for, and it hands us the
    // whole join form's worth of typing time to parse in. A film the browser
    // refused to start proves nothing, so that case waits as it always did.
    if (played) loader.start();
  });

  // Leave cleanly on tab close so the room does not hold a ghost for 10s.
  window.addEventListener("pagehide", () => net.leave());
}

interface EngineLoader {
  /** Begin loading now. Safe to call repeatedly. */
  start(): void;
  /** Resolves once the venue can be walked around. */
  ready: Promise<void>;
}

/**
 * Load the engine on the first sign the fan is actually here: a tap, a
 * keystroke, focusing the name field — or submitting the form.
 *
 * There is deliberately no timer fallback. A page nobody is using should not
 * spend a second of CPU parsing an engine nobody asked for, and the bytes are
 * already arriving either way (see the preload tag in vite.config.ts), so the
 * parse is all that waits.
 */
function engineLoader(net: NetworkClient, loading: LoadingScreen): EngineLoader {
  const events = ["pointerdown", "keydown", "touchstart", "focusin"] as const;
  let start!: () => void;

  const ready = new Promise<void>((resolve) => {
    let started = false;
    start = () => {
      if (started) return;
      started = true;
      for (const name of events) window.removeEventListener(name, start);
      resolve(startEngine(net, loading));
    };
  });

  for (const name of events) {
    window.addEventListener(name, start, { passive: true });
  }

  return { start, ready };
}

/** Load Phaser and stand the venue up. Resolves once it can be walked around. */
async function startEngine(net: NetworkClient, loading: LoadingScreen): Promise<void> {
  loading.set("engine");
  const [{ default: Phaser }, { VenueScene }, { HudScene }] = await Promise.all([
    import("phaser"),
    import("./scenes/VenueScene.js"),
    import("./scenes/HudScene.js"),
  ]);

  loading.set("venue");
  const game = new Phaser.Game({
    /*
     * WEBGL, not AUTO.
     *
     * AUTO silently drops to the Canvas 2D renderer when it cannot get a
     * context, and Canvas has no blend modes and no masks: the lamps lose
     * their glow, the lighting layer composites as nothing, and the venue
     * arrives looking flat and unfinished rather than broken. Nobody reports
     * that as a fault, they just think it looks cheap.
     *
     * We have already proven the device can do this — `hasWebGL()` above
     * created a context and handed it back, and a device that fails there is
     * shown the unsupported screen instead of reaching this line. So a
     * fallback here cannot be "this phone does not support WebGL"; it can only
     * be a context we failed to obtain, and quietly downgrading the evening
     * for everybody is the wrong answer to that.
     */
    type: Phaser.WEBGL,
    parent: "game-root",
    backgroundColor: "#0b1026",
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    render: { pixelArt: true, antialias: false },
    scene: [VenueScene, HudScene],
    callbacks: {
      preBoot: (instance) => {
        instance.scene.start("Venue", { net });
      },
    },
  });

  await new Promise<void>((resolve) => {
    game.events.once("ready", () => resolve());
    // Never leave someone staring at a bar because one event did not fire.
    window.setTimeout(resolve, 8000);
  });

  // Deliberately no hide() here. The engine usually finishes while the fan is
  // still typing, and dropping the curtain is the join flow's call to make —
  // otherwise this resolving mid-join would uncover the card again.
}

/**
 * Phaser needs WebGL; canvas fallback on a 2,000-person venue is not viable.
 *
 * The context this opens is HANDED BACK before returning, and that is the
 * whole point. A browser allows only a handful of live WebGL contexts — on
 * Android Chrome it is a very small number — and one opened here and left to
 * the garbage collector still occupies a slot. Phaser then asks for its own,
 * is refused, and quietly falls back to the Canvas 2D renderer: no blend
 * modes, no masks, so glows render as slabs and a spritesheet draws whole
 * instead of one frame. The venue comes up looking wrong rather than broken,
 * which is far harder to recognise as a fault.
 */
function hasWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ??
      canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl");
    if (!gl) return false;
    // Give the slot back immediately, so the only context that outlives this
    // check is the one the venue actually draws with.
    (gl as WebGLRenderingContext).getExtension("WEBGL_lose_context")?.loseContext();
    canvas.width = 0;
    canvas.height = 0;
    return true;
  } catch {
    return false;
  }
}

function showUnsupported(root: HTMLElement): void {
  root.appendChild(
    el("div", {
      className: "zg-overlay",
      children: [
        el("div", {
          className: "zg-card",
          children: [
            el("h1", { className: "zg-title", text: t("unsupported.title") }),
            el("p", { className: "zg-sub", text: t("unsupported.body") }),
          ],
        }),
      ],
    }),
  );
}
